'use client';

/**
 * Product Lightbox Component
 *
 * Fullscreen overlay for viewing product images and playing YouTube videos.
 * Matches FancyBox behavior from the Magento production site.
 */
import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useCallback, useRef } from 'react';
export interface LightboxItem {
  type: 'image' | 'video';
  url: string;
  title?: string;
}
interface ProductLightboxProps {
  items: LightboxItem[];
  initialIndex: number;
  onClose: () => void;
}
function getVideoEmbedUrl(url: string): string | null {
  const youtubePatterns = [/youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/, /youtu\.be\/([a-zA-Z0-9_-]+)/, /youtube\.com\/embed\/([a-zA-Z0-9_-]+)/];
  for (const pattern of youtubePatterns) {
    const match = url.match(pattern);
    if (match?.[1]) {
      return `https://www.youtube-nocookie.com/embed/${match[1]}?autoplay=1&rel=0&modestbranding=1`;
    }
  }
  // Vimeo page URLs (vimeo.com/123) can't be framed (CSP allows only
  // player.vimeo.com) — convert to the player embed. Matches player URLs too.
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo?.[1]) {
    return `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1`;
  }
  return null;
}
export function ProductLightbox(t0) {
  const $ = _c(37);
  const {
    items,
    initialIndex,
    onClose
  } = t0;
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const backdropRef = useRef(null);
  let t1;
  if ($[0] !== items.length) {
    t1 = () => {
      setCurrentIndex(prev => (prev + 1) % items.length);
    };
    $[0] = items.length;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const goNext = t1;
  let t2;
  if ($[2] !== items.length) {
    t2 = () => {
      setCurrentIndex(prev_0 => (prev_0 - 1 + items.length) % items.length);
    };
    $[2] = items.length;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const goPrev = t2;
  let t3;
  let t4;
  if ($[4] !== goNext || $[5] !== goPrev || $[6] !== onClose) {
    t3 = () => {
      const handleKeyDown = function handleKeyDown(e) {
        bb9: switch (e.key) {
          case "Escape":
            {
              onClose();
              break bb9;
            }
          case "ArrowRight":
            {
              goNext();
              break bb9;
            }
          case "ArrowLeft":
            {
              goPrev();
            }
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    };
    t4 = [onClose, goNext, goPrev];
    $[4] = goNext;
    $[5] = goPrev;
    $[6] = onClose;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  useEffect(_temp, t5);
  const currentItem = items[currentIndex];
  if (!currentItem) {
    return null;
  }
  let t6;
  if ($[10] !== onClose) {
    t6 = e_0 => {
      if (e_0.target === backdropRef.current) {
        onClose();
      }
    };
    $[10] = onClose;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleBackdropClick = t6;
  let t7;
  if ($[12] !== onClose) {
    t7 = <button className="lightbox-close" onClick={onClose} aria-label="Close" type="button">✕</button>;
    $[12] = onClose;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== goPrev || $[15] !== items.length) {
    t8 = items.length > 1 && <button className="lightbox-arrow lightbox-arrow-left" onClick={goPrev} aria-label="Previous" type="button">‹</button>;
    $[14] = goPrev;
    $[15] = items.length;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== currentItem.title || $[18] !== currentItem.type || $[19] !== currentItem.url) {
    t9 = <div className="lightbox-content">{currentItem.type === "image" ? <img src={currentItem.url} alt={currentItem.title || "Product image"} className="lightbox-image" /> : <div className="lightbox-video-wrapper"><iframe key={currentItem.url} src={getVideoEmbedUrl(currentItem.url) ?? currentItem.url} title={currentItem.title || "Product video"} className="lightbox-video" allow="autoplay; fullscreen; encrypted-media" allowFullScreen={true} /></div>}</div>;
    $[17] = currentItem.title;
    $[18] = currentItem.type;
    $[19] = currentItem.url;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== goNext || $[22] !== items.length) {
    t10 = items.length > 1 && <button className="lightbox-arrow lightbox-arrow-right" onClick={goNext} aria-label="Next" type="button">›</button>;
    $[21] = goNext;
    $[22] = items.length;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== currentIndex || $[25] !== items.length) {
    t11 = items.length > 1 && <div className="lightbox-counter">{currentIndex + 1} / {items.length}</div>;
    $[24] = currentIndex;
    $[25] = items.length;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== currentItem.title) {
    t12 = currentItem.title && <div className="lightbox-title">{currentItem.title}</div>;
    $[27] = currentItem.title;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== handleBackdropClick || $[30] !== t10 || $[31] !== t11 || $[32] !== t12 || $[33] !== t7 || $[34] !== t8 || $[35] !== t9) {
    t13 = <div ref={backdropRef} className="lightbox-backdrop" onClick={handleBackdropClick} role="dialog" aria-modal="true" aria-label="Media viewer">{t7}{t8}{t9}{t10}{t11}{t12}</div>;
    $[29] = handleBackdropClick;
    $[30] = t10;
    $[31] = t11;
    $[32] = t12;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  return t13;
}
function _temp() {
  const original = document.body.style.overflow;
  document.body.style.overflow = "hidden";
  return () => {
    document.body.style.overflow = original;
  };
}